import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Target, BookOpen, Clock, FileText } from 'lucide-react';

export function AssessmentBlueprint({ data, metadata, type = 'assessment' }) {
  if (!data) return null;

  const slos = data.slos || [];
  const isExam = type === 'exam';
  const title = data.title || `${metadata?.subject || ''} ${type.charAt(0).toUpperCase() + type.slice(1)}`;
  const totalMarks = data.total_marks || metadata?.total_marks || 0;
  const duration = data.time_limit || data.duration || metadata?.duration_minutes || '';
  const sections = data.sections || [];

  // Count questions per SLO
  const sloStats = slos.map(slo => {
    const qNums = slo.questions || [];
    let marks = 0;
    if (isExam && sections.length > 0) {
      sections.forEach(sec => {
        (sec.questions || []).forEach(q => {
          if (qNums.includes(q.number)) marks += (q.marks || 1);
        });
      });
    } else if (data.questions) {
      data.questions.forEach(q => {
        if (qNums.includes(q.number)) marks += (q.marks || 1);
      });
    }
    return { ...slo, marks, count: qNums.length };
  });

  // Question type breakdown
  const typeBreakdown = {};
  if (isExam) {
    sections.forEach(sec => {
      (sec.questions || []).forEach(q => {
        const t = q.type || sec.question_type || 'general';
        typeBreakdown[t] = (typeBreakdown[t] || 0) + 1;
      });
    });
  } else if (data.questions) {
    data.questions.forEach(q => {
      const t = q.type || 'general';
      typeBreakdown[t] = (typeBreakdown[t] || 0) + 1;
    });
  }

  const totalQuestions = isExam
    ? sections.reduce((sum, s) => sum + (s.questions?.length || 0), 0)
    : (data.questions?.length || 0);

  return (
    <Card className="border-2 border-blue-200 bg-gradient-to-b from-blue-50 to-white mb-6 print:break-after-page" data-testid="assessment-blueprint">
      <CardContent className="p-6">
        {/* Header */}
        <div className="text-center mb-6 pb-4 border-b-2 border-blue-200">
          <h2 className="text-xl font-bold text-blue-900 uppercase tracking-wide" data-testid="blueprint-title">
            Assessment Blueprint
          </h2>
          <h3 className="text-lg font-semibold text-gray-800 mt-1">{title}</h3>
          <div className="flex justify-center gap-4 mt-3 text-sm text-gray-600">
            {metadata?.subject && <span><BookOpen className="h-4 w-4 inline mr-1" />{metadata.subject}</span>}
            {metadata?.year_level && <span>Year {metadata.year_level}</span>}
            {metadata?.term && <span>Term {metadata.term}</span>}
          </div>
        </div>

        {/* Summary Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-blue-100 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-800">{totalQuestions}</p>
            <p className="text-xs text-blue-600 font-medium">Total Questions</p>
          </div>
          <div className="bg-green-100 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-800">{totalMarks}</p>
            <p className="text-xs text-green-600 font-medium">Total Marks</p>
          </div>
          <div className="bg-purple-100 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-purple-800">{slos.length}</p>
            <p className="text-xs text-purple-600 font-medium">SLOs Covered</p>
          </div>
          <div className="bg-amber-100 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-800">{duration}</p>
            <p className="text-xs text-amber-600 font-medium">{typeof duration === 'number' ? 'Minutes' : 'Duration'}</p>
          </div>
        </div>

        {/* SLO Table */}
        {sloStats.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-purple-600" />
              Student Learning Outcomes (SLOs)
            </h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-purple-50">
                    <th className="text-left px-3 py-2 font-semibold text-purple-800 w-20">SLO</th>
                    <th className="text-left px-3 py-2 font-semibold text-purple-800">Description</th>
                    <th className="text-center px-3 py-2 font-semibold text-purple-800 w-24">Questions</th>
                    <th className="text-center px-3 py-2 font-semibold text-purple-800 w-20">Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {sloStats.map((slo, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs font-bold">{slo.code}</Badge>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{slo.description}</td>
                      <td className="px-3 py-2 text-center">
                        <span className="text-xs text-gray-500">
                          Q{(slo.questions || []).join(', Q')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center font-semibold">{slo.marks}</td>
                    </tr>
                  ))}
                  <tr className="bg-purple-50 font-semibold">
                    <td className="px-3 py-2" colSpan={2}>Total SLOs: {sloStats.length}</td>
                    <td className="px-3 py-2 text-center">{totalQuestions} Qs</td>
                    <td className="px-3 py-2 text-center">{sloStats.reduce((s, slo) => s + slo.marks, 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Exam Sections Breakdown (for exams) */}
        {isExam && sections.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <FileText className="h-4 w-4 text-blue-600" />
              Exam Structure
            </h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-blue-50">
                    <th className="text-left px-3 py-2 font-semibold text-blue-800">Section</th>
                    <th className="text-center px-3 py-2 font-semibold text-blue-800 w-24">Questions</th>
                    <th className="text-center px-3 py-2 font-semibold text-blue-800 w-20">Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((sec, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-gray-700">{sec.name || sec.title}</td>
                      <td className="px-3 py-2 text-center">{sec.questions?.length || sec.num_questions || '?'}</td>
                      <td className="px-3 py-2 text-center font-semibold">{sec.marks}</td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50 font-semibold">
                    <td className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 text-center">{totalQuestions}</td>
                    <td className="px-3 py-2 text-center">{sections.reduce((s, sec) => s + (sec.marks || 0), 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Question Type Distribution */}
        {Object.keys(typeBreakdown).length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Question Type Distribution
            </h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(typeBreakdown).map(([type, count]) => (
                <Badge key={type} variant="outline" className="text-sm py-1 px-3">
                  {type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: {count}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
